import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  quizQuestion,
  quizStatistic,
  submitQuiz,
  updateCompletion,
} from '../services/QuizService';
import { ArrowBack, ArrowBackIos } from '@mui/icons-material';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { LoadingIndicator, Routes } from '@xolv-mybrightlink/common';
import { FormattedMessage, useIntl } from 'react-intl';
import { Quiz, StatisticData } from '../models/Course';
import { handlerError } from '../utils/errorHandler';
import { QuizStep } from './step';

export function QuizPage({ onBack }: { onBack: () => void }) {
  const { courseId, lessonId, quizId } = useParams<{
    courseId: string;
    lessonId: string;
    quizId: string;
  }>();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [answerMap, setAnswer] = useState<
    Record<number, Record<string, string | number | boolean>>
  >({});

  const showFinishButton = useMemo(() => {
    return Object.keys(answerMap).length > 0;
  }, [answerMap]);
  const [loading, setLoading] = useState<boolean>(false);
  const [validate, setValidate] = useState<number>(QuizStep.INITIAL);
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    (async function () {
      if (!quizId) return;

      try {
        setLoading(true);
        const quizJson = await quizQuestion(quizId);
        setQuizzes(quizJson);
        quizJson.forEach((quiz: Quiz) => {
          setAnswer((prev) => Object.assign(prev, { [quiz.id]: {} }));
        });
      } catch (e) {
        handlerError('Failed to load quiz');
      } finally {
        setLoading(false);
      }
    })();
  }, [quizId]);

  function _resetQuiz() {
    setValidate(QuizStep.INITIAL);
    quizzes.forEach((quiz: Quiz) => {
      setAnswer((prev) => Object.assign(prev, { [quiz.id]: {} }));
    });
  }

  async function _submitQuiz() {
    if (!courseId || !lessonId || !quizId) return;

    // send data to lms server
    const data = quizzes.map((quiz: Quiz) => {
      const data = (answerMap[quiz.id]?.data ?? {}) as unknown as Record<
        string,
        number
      >;
      return {
        questionId: quiz.id,
        points: quiz.points,
        time: 0,
        correct: 1,
        possiblePoints: quiz.points,
        data,
      };
    });

    try {
      setLoading(true);
      await submitQuiz(lessonId, quizId, data);
      await updateCompletion(lessonId, quizId);
      navigate(Routes.resources.courses.courseId(+courseId), { replace: true });
    } catch (e) {
      handlerError('Failed to submit quiz!');
    } finally {
      setLoading(false);
    }
  }

  async function _validateQuiz() {
    if (!quizId) return;

    const statisticData: StatisticData = {};
    quizzes.forEach((quiz: Quiz) => {
      const item = answerMap[quiz.id];
      statisticData[quiz.id] = {
        correct: item.correct ? 1 : 0,
        points: quiz.points,
      };
    });

    try {
      setLoading(true);
      // send statistic data to lms server
      await quizStatistic(quizId, statisticData);
    } catch (e) {
      handlerError('Failed to send quiz statistic!');
    } finally {
      setLoading(false);
    }

    // check answer is correct or not
    const keys = Object.keys(answerMap);
    const isCorrect = keys.every((key) => {
      const answer = answerMap[+key];
      return answer.correct ?? false;
    });
    setValidate(!isCorrect ? QuizStep.FAILED : QuizStep.COMPLETED);
  }

  const buttonStyle = {
    py: 2,
    px: 20,
    borderRadius: 7.5,
    width: 'fit-content',
  };

  const showData = !loading && quizzes.length > 0;
  const isInitial = validate === QuizStep.INITIAL;
  const isFailed = validate === QuizStep.FAILED;
  const isCompleted = validate === QuizStep.COMPLETED;
  type Answer = { answer: string; correct: boolean };

  function bgColor(isActive: boolean, answer: Answer): string {
    if (isInitial && isActive) return '#307fe2';
    if (isFailed && answer.correct && isActive) return '#307fe2';
    if (isFailed && answer.correct) return '#00b2a9';
    if (isCompleted && answer.correct) return '#307fe2';
    return '#fff';
  }

  function textColor(isActive: boolean, answer: Answer): string {
    if (isInitial && !isActive) return '#3b4559';
    if (!isInitial && !answer.correct) return '#3b4559';
    return '#fff';
  }

  function border(
    isActive: boolean,
    answer: Answer,
  ): { width: number; color: string } {
    const defaultBorder = { width: 1, color: '#d8dade' };
    if (isFailed && isActive && !answer.correct)
      return { width: 1.5, color: '#d00070' };
    if (isCompleted && !answer.correct) return defaultBorder;
    return defaultBorder;
  }

  function fontWeight(isActive: boolean, answer: Answer): string {
    const defaultValue = 'normal';
    if (isInitial && isActive) return '600';
    if (!isInitial && answer.correct) return '600';
    if (isActive && !answer.correct) return defaultValue;
    return defaultValue;
  }

  const totalCorrectAnswer = useMemo(() => {
    return Object.keys(answerMap).reduce((acc, key) => {
      return answerMap[+key].correct ? acc + 1 : acc;
    }, 0);
  }, [answerMap]);

  return (
    <Box sx={{ height: '100%' }}>
      <Typography
        onClick={onBack}
        sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
      >
        <ArrowBackIos sx={{ color: '#307fe2' }} />
        <FormattedMessage
          id={
            validate !== QuizStep.INITIAL
              ? 'resources.course.quiz.result.title'
              : 'resources.course.quiz.title'
          }
        />
      </Typography>

      {loading && (
        <LoadingIndicator
          sx={{
            height: '100%',
          }}
        />
      )}

      {showData && (
        <Box sx={{ height: '100%', overflowY: 'auto', pr: 9.5, mr: -9.5 }}>
          {!isInitial && (
            <Stack direction='row' sx={{ pt: 11.25 }}>
              <Typography variant='body2' sx={{ color: '#3b4559' }}>
                <Typography component='span' fontWeight={600}>
                  {`${totalCorrectAnswer}`.concat(' ')}
                </Typography>
                <Typography component='span'>
                  {intl
                    .formatMessage({
                      id: 'resources.course.quiz.result.count.of',
                    })
                    .concat(' ')}
                </Typography>
                <Typography component='span' fontWeight={600}>
                  {`${quizzes.length}`.concat(' ')}
                </Typography>
                <Typography component='span'>
                  {intl
                    .formatMessage(
                      { id: 'resources.course.quiz.result.count.total' },
                      {
                        total: quizzes.length,
                      },
                    )
                    .concat(' ')}
                </Typography>
                <Typography component='span'>
                  {intl.formatMessage({
                    id: 'resources.course.quiz.result.count.answer.correct',
                  })}
                </Typography>
                <Typography
                  component='span'
                  sx={{
                    color:
                      totalCorrectAnswer == quizzes.length
                        ? '#00b2a9'
                        : '#d00070',
                    fontWeight: 500,
                  }}
                >
                  &nbsp;{`(${(totalCorrectAnswer / quizzes.length) * 100}%)`}
                </Typography>
              </Typography>
            </Stack>
          )}

          {quizzes.map((quiz, index) => (
            <Box key={quiz.id}>
              {isInitial && (
                <Typography sx={{ mt: 5 }}>
                  <Typography component='span'>
                    {intl
                      .formatMessage({
                        id: 'resources.course.quiz.question',
                      })
                      .concat(' ')}
                  </Typography>
                  <Typography component='span' fontWeight={600}>
                    {`${index + 1}`.concat(' ')}
                  </Typography>
                  <Typography component='span'>
                    {intl
                      .formatMessage({
                        id: 'resources.course.quiz.result.count.of',
                      })
                      .concat(' ')}
                  </Typography>
                  <Typography component='span' fontWeight={600}>
                    {quizzes.length}
                  </Typography>
                </Typography>
              )}
              <Typography sx={{ mt: 5, fontWeight: 500 }} variant='h6'>
                {quiz.question}
              </Typography>
              <Box sx={{ mt: 5 }}>
                <Box sx={{ mt: 5 }} />
                {(quiz.answer_data ?? []).map((answer, index) => {
                  const isActive = answerMap[quiz.id]
                    ? answerMap[quiz.id].active === answer.answer
                    : false;

                  const itemBorder = border(isActive, answer);

                  return (
                    <Box
                      key={`q-${quiz.id}-${index}`}
                      sx={{
                        px: 4,
                        py: 5,
                        mb: 4,
                        borderRadius: '42px',
                        boxShadow:
                          isActive || isFailed || isCompleted
                            ? 'none'
                            : '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
                        border: `${itemBorder.width}px solid ${itemBorder.color}`,
                        bgcolor: bgColor(isActive, answer),
                        userSelect: 'none',
                      }}
                      onClick={() => {
                        if (!isInitial) return;
                        setAnswer((m) => {
                          const _answerMap: Record<string, number> = {};
                          _.range(quiz.answer_data.length).forEach((i) => {
                            _answerMap[i.toString()] = 0;
                          });
                          _answerMap[index.toString()] = 1;
                          return Object.assign({}, m, {
                            [quiz.id]: {
                              active: answer.answer,
                              correct: answer.correct,
                              data: _answerMap,
                            },
                          });
                        });
                      }}
                    >
                      <Typography
                        sx={{
                          color: textColor(isActive, answer),
                          fontWeight: fontWeight(isActive, answer),
                        }}
                      >
                        {answer.answer}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              mt: 20,
              pb: 10,
            }}
          >
            {isInitial && showFinishButton && (
              <Box
                onClick={_validateQuiz}
                sx={{
                  ...buttonStyle,
                  bgcolor: '#307fe2',
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                  <FormattedMessage id='resources.course.quiz.finish' />
                </Typography>
              </Box>
            )}

            {isFailed && (
              <Box
                onClick={_resetQuiz}
                sx={{
                  ...buttonStyle,
                  bgcolor: '#307fe2',
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                  <FormattedMessage id='resources.course.quiz.result.restart' />
                </Typography>
              </Box>
            )}

            {isCompleted && (
              <Box
                sx={{
                  width: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 10,
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <Box
                  onClick={_resetQuiz}
                  sx={{
                    ...buttonStyle,
                    border: '1px solid #307fe2',
                  }}
                >
                  <Typography sx={{ color: '#307fe2', fontWeight: 500 }}>
                    <FormattedMessage id='resources.course.quiz.result.restart' />
                  </Typography>
                </Box>
                <Box
                  onClick={_submitQuiz}
                  sx={{
                    ...buttonStyle,
                    bgcolor: '#307fe2',
                  }}
                >
                  <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                    <FormattedMessage id='resources.course.quiz.result.continue' />
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {!showData && !loading && (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src='/public/assets/images/dog_not_found.svg' height='30%' />
          <Typography
            sx={{
              mt: 20,
              color: '#626a7a',
            }}
          >
            <FormattedMessage id='resources.course.quiz.page-not-found' />
          </Typography>
          <Button
            variant='contained'
            disableElevation
            onClick={() => navigate(-1)}
            sx={{
              mt: 10,
              borderRadius: 30,
              py: 3.5,
              width: 200,
            }}
          >
            <ArrowBack sx={{ mr: 2.5 }} />
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <FormattedMessage id='common.go-back' />
            </Typography>
          </Button>
        </Box>
      )}
    </Box>
  );
}
